import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Select, message } from "antd";
import AdminJewelryReportService from "../../../services/admin-jewelryReport-service";
import ReportScaffold from "../../ChartsGroup/DiamondReportsCharts/_shared/ReportScaffold";
import { exportChartPdf } from "../../ChartsGroup/DiamondReportsCharts/_shared/exportChartPdf";
import { BlobToDownloadFile } from "../../../helper/commanFunction";
import { GLOBAL_CATEGORY_CONFIG } from "./_shared/globalJewelryReportConfigs";
import JewelryDetailSearchTable from "../jewelry/_shared/JewelryDetailSearchTable";
import { extractDealerIds, fetchPriorPeriodTotalClicks } from "../jewelry/_shared/jewelryHelpers";

const { Option } = Select;

// Global scope: filter-select visibility is a 3-way branch on the logged-in
// dealer's own membership type (Admin -> both selects; Vendor -> Retailer(s)
// only; anything else, e.g. Retailer -> Vendor(s) only), ported as-is from
// the pre-redesign page -- a different rule shape than the Dealer scope's
// two independent isVendor/isRetailer checks, not something to "fix" here.
const GlobalJewelryCategoryReport = () => {
  const { t } = useTranslation();
  const loginDetials = useSelector((state) => state.loginReducer);

  const [dealerId, setDealerId] = useState();
  const [forDealer, setForDealer] = useState("");
  const [dealerMemberTypeName, setDealerMemberTypeName] = useState("");
  const [dataRangeFrom, setDataRangeFrom] = useState(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [dataRangeTo, setDataRangeTo] = useState(moment().format("YYYY-MM-DD"));
  const [vendorIds, setVendorIds] = useState([]);
  const [retailerIds, setRetailerIds] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [retailerList, setRetailerList] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [totalClicks, setTotalClicks] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refetchToken, setRefetchToken] = useState(0);
  const [filterValue, setFilterValue] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [activityPdfLoading, setActivityPdfLoading] = useState(false);
  const [chartType, setChartType] = useState("doughnut");
  const [priorTotalClicks, setPriorTotalClicks] = useState(null);

  useEffect(() => {
    if (loginDetials?.loginDetials?.responseData?.logInSucceeded) {
      const id = loginDetials.loginDetials.responseData.dealerId;
      setDealerId(id);
      setDealerMemberTypeName(loginDetials.loginDetials.responseData.dealerMemberTypeName);
      handleGetForDealer(id);
    }
    handleGetVendorDropdownData();
    handleGetRetailerDropdownData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetForDealer = (id) => {
    AdminJewelryReportService.GetForDealer({ dealerID: id }).then((response) => {
      if (response.data.message === "Success") {
        const fd = response.data.responseData.forDealer;
        setForDealer(fd);
        handleLoadCategoryData(id, fd, dataRangeFrom, dataRangeTo, [], []);
      }
    });
  };

  const handleGetVendorDropdownData = () => {
    const id = loginDetials?.loginDetials?.responseData?.dealerId;
    AdminJewelryReportService.GetVendorDropdownList({ dealerId: id, reporttype: "global" }).then((response) => {
      if (response.data.message === "Success") setVendorList(response.data.responseData);
    });
  };

  const handleGetRetailerDropdownData = () => {
    AdminJewelryReportService.GetRetailerDropdownList({ dealerId: 0, reporttype: "global" }).then((response) => {
      if (response.data.message === "Success") setRetailerList(response.data.responseData);
    });
  };

  const handleVendorChange = (values) => {
    setVendorIds(values);
    if (values.length === 0) {
      handleGetRetailerDropdownData();
    } else {
      AdminJewelryReportService.ChangeRetailerList({ vendorIds: extractDealerIds(values).toString() }).then((response) => {
        if (response.data.message === "Success") setRetailerList(response.data.responseData);
      });
    }
  };

  const handleRetailerChange = (values) => {
    setRetailerIds(values);
    if (values.length === 0) {
      handleGetVendorDropdownData();
    } else {
      AdminJewelryReportService.ChangeVendorList({ retailerIds: extractDealerIds(values).toString() }).then((response) => {
        if (response.data.message === "Success") setVendorList(response.data.responseData);
      });
    }
  };

  const handleLoadCategoryData = (dealerIdArg, forDealerArg, fromDate, toDate, vIds, rIds) => {
    setLoading(true);
    AdminJewelryReportService.GetClicksBasedOnCategory({
      dealerID: dealerIdArg,
      forDealer: forDealerArg,
      fromDate: moment(fromDate).format("YYYY-MM-DD"),
      toDate: moment(toDate).format("YYYY-MM-DD"),
      retailerIds: extractDealerIds(rIds).toString(),
      vendorIds: extractDealerIds(vIds).toString(),
    })
      .then((response) => {
        setLoading(false);
        if (response.data.message === "Success" && response.data.responseData) {
          const rows = response.data.responseData;
          setCategoryData(rows);
          setTotalClicks(rows.reduce((sum, row) => sum + Number(row.count || 0), 0));
        } else {
          setCategoryData([]);
          setTotalClicks(0);
        }
      })
      .catch(() => {
        setLoading(false);
        setCategoryData([]);
        setTotalClicks(0);
      });

    setPriorTotalClicks(null);
    fetchPriorPeriodTotalClicks(
      AdminJewelryReportService.GetClicksBasedOnCategory,
      (pFrom, pTo) => ({
        dealerID: dealerIdArg,
        forDealer: forDealerArg,
        fromDate: pFrom,
        toDate: pTo,
        retailerIds: extractDealerIds(rIds).toString(),
        vendorIds: extractDealerIds(vIds).toString(),
      }),
      fromDate,
      toDate,
      "count",
      (data) => data
    ).then(setPriorTotalClicks);
  };

  const onDateChange = (dates, dateStrings) => {
    if (dates) {
      setDataRangeFrom(dateStrings[0]);
      setDataRangeTo(dateStrings[1]);
    }
  };

  const handleApplyFilter = () => {
    setFilterValue("");
    handleLoadCategoryData(dealerId, forDealer, dataRangeFrom, dataRangeTo, vendorIds, retailerIds);
    setRefetchToken((tok) => tok + 1);
  };

  const handleSliceClick = (category) => setFilterValue(category);

  const handleDownloadChart = async () => {
    setPdfLoading(true);
    const vendorNames = vendorIds.length === 0 ? t("All Vendors") : vendorList.filter((v) => extractDealerIds(vendorIds).includes(v.dealerID.toString())).map((v) => v.dealerCompany).join(", ");
    const retailerNames = retailerIds.length === 0 ? t("All Retailers") : retailerList.filter((v) => extractDealerIds(retailerIds).includes(v.dealerID.toString())).map((v) => v.dealerCompany).join(", ");
    try {
      await exportChartPdf({
        elementId: "pdf-content",
        title: t(GLOBAL_CATEGORY_CONFIG.pageTitle),
        dealerCompany: loginDetials?.loginDetials?.responseData?.dealerCompany,
        dealerId,
        dataRangeFrom,
        dataRangeTo,
        infoLines: [
          ...(dealerMemberTypeName !== "Vendor" ? [`Vendors: ${vendorNames}`] : []),
          ...(dealerMemberTypeName === "Admin" ? [`Retailers: ${retailerNames}`] : []),
          `Total Clicks: ${totalClicks}`,
        ],
        fileName: "Global-Jewelry-Category-Report.pdf",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadActivityPdf = () => {
    let inputData = {
      dealerID: dealerId,
      forDealer: forDealer,
      fromDate: moment(dataRangeFrom).format("YYYY-MM-DD"),
      toDate: moment(dataRangeTo).format("YYYY-MM-DD"),
      retailerIds: extractDealerIds(retailerIds).toString(),
      vendorIds: extractDealerIds(vendorIds).toString(),
      reportName: "Category",
      barViewDimensions: chartType === "bar" ? "Category" : "",
    };
    setActivityPdfLoading(true);
    AdminJewelryReportService.ExportMetricPdf(inputData)
      .then((response) => {
        setActivityPdfLoading(false);
        const filename = `GlobalJewelry_Category_${dataRangeFrom}_${dataRangeTo}.pdf`;
        BlobToDownloadFile(response.data, filename, "pdf");
      })
      .catch(() => {
        setActivityPdfLoading(false);
        message.error(t("Failed to generate PDF. Please try again."));
      });
  };

  const sortedCategories = [...categoryData].sort((a, b) => Number(b.count || 0) - Number(a.count || 0));
  const topCategory = sortedCategories[0];
  const top3Sum = sortedCategories.slice(0, 3).reduce((sum, row) => sum + Number(row.count || 0), 0);
  const top3Pct = totalClicks > 0 ? Math.round((top3Sum / totalClicks) * 100) : 0;
  const topPct = totalClicks > 0 && topCategory ? ((Number(topCategory.count) / totalClicks) * 100).toFixed(2) : 0;

  return (
    <ReportScaffold
      config={GLOBAL_CATEGORY_CONFIG}
      report={{
        [GLOBAL_CATEGORY_CONFIG.dataKey]: categoryData,
        JewelryTotalClicks: [{ TotalClickCnt: totalClicks }],
        JewelrySummaryKPIs: priorTotalClicks != null ? [{ Metric: "TotalClicks", CurrentValue: totalClicks, PriorValue: priorTotalClicks }] : [],
      }}
      loading={loading}
      dealerId={dealerId}
      forDealer={forDealer}
      dealerCompany={loginDetials?.loginDetials?.responseData?.dealerCompany}
      globalDiamonds={{ dataRangeFrom, dataRangeTo }}
      onDateChange={onDateChange}
      onApplyFilter={handleApplyFilter}
      refetchToken={refetchToken}
      onDownloadChart={handleDownloadChart}
      downloadingChart={pdfLoading}
      onDownloadActivityPdf={handleDownloadActivityPdf}
      downloadingActivityPdf={activityPdfLoading}
      onChartClick={handleSliceClick}
      onChartTypeChange={setChartType}
      insight={
        topCategory && (
          <div className="edr-insight">
            💡 <b>{topCategory.category}</b> {t("leads category demand at")} <b>{topPct}%</b>, {t("and the top 3 categories capture")}{" "}
            <b>{top3Pct}%</b> {t("of engagement")} —{" "}
            {top3Pct > 70
              ? t("a highly concentrated demand signal worth prioritising in merchandising & inventory.")
              : t("demand is reasonably spread; there is a long tail to develop.")}
          </div>
        )
      }
      extraFilterFields={null}
      /* Bug 1840: Vendor(s)/Retailer(s) dropdowns removed from Global reports
          (expose vendor/retailer private data at the Global scope) - kept here
          commented out in case this filter is needed again in the future.
          NOTE: extraFilterFields={null} above is required (not just omitting the
          prop) - ReportScaffold falls back to a Diamond-only "Select Diamond"
          filter when this prop is undefined, which doesn't belong on Jewelry reports.
      extraFilterFields={
        <>
          {dealerMemberTypeName === "Admin" && (
            <>
              <div className="col-md-3">
                <div className="input__block">
                  <label>{t("Vendor(s)")}</label>
                  <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select One or More Vendors")} optionFilterProp="children" value={vendorIds} onChange={handleVendorChange}>
                    {vendorList.map((item) => (
                      <Option key={`vendor-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="col-md-3">
                <div className="input__block">
                  <label>{t("Retailer(s)")}</label>
                  <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select one or more retailer")} optionFilterProp="children" value={retailerIds} onChange={handleRetailerChange}>
                    {retailerList.map((item) => (
                      <Option key={`retailer-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                    ))}
                  </Select>
                </div>
              </div>
            </>
          )}
          {dealerMemberTypeName === "Vendor" && (
            <div className="col-md-3">
              <div className="input__block">
                <label>{t("Retailer(s)")}</label>
                <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select one or more retailer")} optionFilterProp="children" value={retailerIds} onChange={handleRetailerChange}>
                  {retailerList.map((item) => (
                    <Option key={`retailer-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                  ))}
                </Select>
              </div>
            </div>
          )}
          {dealerMemberTypeName !== "Admin" && dealerMemberTypeName !== "Vendor" && (
            <div className="col-md-3">
              <div className="input__block">
                <label>{t("Vendor(s)")}</label>
                <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select one or more vendor")} optionFilterProp="children" value={vendorIds} onChange={handleVendorChange}>
                  {vendorList.map((item) => (
                    <Option key={`vendor-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                  ))}
                </Select>
              </div>
            </div>
          )}
        </>
      }
      */
      detailTable={
        <JewelryDetailSearchTable
          dealerId={dealerId}
          forDealer={forDealer}
          dataRangeFrom={dataRangeFrom}
          dataRangeTo={dataRangeTo}
          vendorIds={vendorIds}
          retailerIds={retailerIds}
          dealerMembershipType={dealerMemberTypeName}
          filterParamName={GLOBAL_CATEGORY_CONFIG.detail.filterParamName}
          filterValue={filterValue}
          fileNamePart={GLOBAL_CATEGORY_CONFIG.detail.fileNamePart}
          dealerCompany={loginDetials?.loginDetials?.responseData?.dealerCompany}
          refetchToken={refetchToken}
          reportType="Global"
          title={t("Product Click Log")}
        />
      }
    />
  );
};

export default GlobalJewelryCategoryReport;
